<template>
  <div class="container">
    <h1>Overall Statistics</h1>
    <p class="subtitle">Aggregated SMS statistics across all users</p>

    <div class="row margin-bottom-2">
      <div class="col-xs-3">
        <sdx-input
          id="periodValue"
          class="filter-input"
          type="number"
          min="1"
          :value="periodCount"
          @input="onPeriodCountChange"
        ></sdx-input>
      </div>
      <div class="col-xs-3">
        <sdx-select id="periodMode" :value="mode" class="filter-input" @input="onModeChange">
          <sdx-select-option value="days" :selected="mode === 'days'">Days</sdx-select-option>
          <sdx-select-option value="months" :selected="mode === 'months'">Months</sdx-select-option>
        </sdx-select>
      </div>
      <div class="col-xs-3">
        <sdx-button
            id="buttoShowStatistic"
            data-testId="buttoShowStatistic"
            label="Show"
            @click="loadStatistics()"
        ></sdx-button>
      </div>
    </div>

    <sdx-loading-spinner id="spinner-overall-statistics" size="large" sr-hint="Loading. Please wait."></sdx-loading-spinner>

    <div v-if="showStatistics" class="summary">
      <div class="summary-card">
        <div class="summary-label">Total SMS</div>
        <div class="summary-value">{{    formatBigNumber(totalUsage) }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Total CHF</div>
        <div class="summary-value">{{ totalSumChf }}</div>
      </div>
    </div>

    <div v-if="showStatistics">
      <div class="sms-statistic-chart">
        <canvas id="overall-sms-chart"></canvas>
      </div>
      <div class="sms-statistic-chart" v-if="showStatisticsChf">
        <canvas id="overall-chf-chart"></canvas>
      </div>
    </div>
    <div v-if="showStatistics" class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th>Total Usage</th>
            <th>Swisscom</th>
            <th>Domestic</th>
            <th>International</th>
            <th>Blocked</th>
            <th>CHF</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="period in periods" :key="period.date">
            <td>{{ period.date }}</td>
            <td>{{ Number(period.totalUsage || 0) }}</td>
            <td>{{ getUsage(period, 'Swisscom') }}</td>
            <td>{{ getUsage(period, 'Domestic') }}</td>
            <td>{{ getUsage(period, 'International') }}</td>
            <td>{{ getUsage(period, 'Blocked') }}</td>
            <td>{{ getTotalSum(period) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import overallStatisticsService from '@/modules/overallStatistics/service';
import loadingSpinnerService from '@/services/loading-spinner.service';
import {showAlert} from '@/services/notification.service';
import { Chart, registerables } from 'chart.js';
import {formatBigNumber} from "../../../util/utility";
Chart.register(...registerables);

export default {
  data() {
    return {
      mode: 'days',
      periodCount: 10,
      statistic: null,
      showStatistics: false,
      showStatisticsChf: false,
      chartSms: null,
      chartChf: null
    };
  },
  computed: {
    periods() {
      return this.statistic?.months || this.statistic?.days || [];
    },
    totalUsage() {
      return this.periods.reduce((sum, period) => {
        return sum + Number(period.totalUsage || 0);
      }, 0);
    },
    totalSumChf() {
      const sum = this.periods.reduce((total, period) => {
        return total + this.getTotalSum(period);
      }, 0);
      return sum.toFixed(2);
    }
  },
  mounted() {
    this.loadStatistics();
  },
  methods: {
    formatBigNumber,
    onPeriodCountChange(event) {
      const value = Number(event.target.value);
      if (Number.isNaN(value) || value < 1) {
        return;
      }
      this.periodCount = value;
    },
    getSdxSelectValue(event) {
      const rawValue = event?.target?.value ?? event?.detail?.value ?? event?.detail;
      if (Array.isArray(rawValue)) {
        return rawValue[0];
      }
      if (rawValue && typeof rawValue === 'object' && rawValue.value) {
        return rawValue.value;
      }
      return rawValue;
    },
    onModeChange(event) {
      const nextMode = this.getSdxSelectValue(event);
      if (!nextMode || nextMode === this.mode) {
        return;
      }

      if (this.mode === 'months' && nextMode === 'days') {
        this.periodCount = this.periodCount * 30;
      } else if (this.mode === 'days' && nextMode === 'months') {
        this.periodCount = Math.max(1, Math.round(this.periodCount / 30));
      }

      this.mode = nextMode;
    },
    async loadStatistics() {
      loadingSpinnerService.show('overall-statistics');
      try {
        if (this.mode === 'days') {
          this.statistic = await overallStatisticsService.getAllUsersStatsByDays(this.periodCount);
        } else {
          this.statistic = await overallStatisticsService.getAllUsersStatsByMonths(this.periodCount);
        }
        this.showStatistics = false;
        this.showStatisticsChf = false;
        await this.$nextTick();
        this.showStatistics = true;
        this.showStatisticsChf = this.hasChfData();
        this.$nextTick(() => {
          this.renderCharts();
        });
      } catch (error) {
        this.showStatistics = false;
        if (error?.message === '403 Unauthorized') {
          showAlert('403 Unauthorized: Access to overall statistics was denied.');
          return;
        }
        showAlert(error?.message || 'Overall statistics could not be loaded.');
      } finally {
        loadingSpinnerService.hide('overall-statistics');
      }
    },
    hasChfData() {
      return this.periods.some(period => {
        const details = period.details || [];
        return details.some(detail => Number(detail.sum || 0) > 0);
      });
    },
    renderCharts() {
      // Destroy old charts if they exist
      if (this.chartSms) {
        this.chartSms.destroy();
      }
      if (this.chartChf) {
        this.chartChf.destroy();
      }
      // Prepare SMS chart
      const ctxSms = document.getElementById('overall-sms-chart');
      if (ctxSms) {
        this.chartSms = new Chart(ctxSms, {
          type: 'bar',
          data: {
            labels: this.periods.map(p => p.date),
            datasets: [
              this.getSmsDataset('Swisscom', '#1781E3'),
              this.getSmsDataset('Domestic', '#86BFF2'),
              this.getSmsDataset('International', '#BFDDF9'),
              this.getSmsDataset('Blocked', '#CFE0C5')
            ]
          },
          options: this.getChartOptions('SMS')
        });
      }
      // Prepare CHF chart
      if (this.showStatisticsChf) {
        const ctxChf = document.getElementById('overall-chf-chart');
        if (ctxChf) {
          this.chartChf = new Chart(ctxChf, {
            type: 'bar',
            data: {
              labels: this.periods.map(p => p.date),
              datasets: [
                this.getChfDataset('Swisscom', '#6F9953'),
                this.getChfDataset('Domestic', '#A3C28D'),
                this.getChfDataset('International', '#CFE0C5'),
                this.getChfDataset('Blocked', '#a0b0bb')
              ]
            },
            options: this.getChartOptions('CHF')
          });
        }
      }
    },
    getSmsDataset(destType, color) {
      return {
        label: destType,
        backgroundColor: color,
        stack: 'sms',
        yAxisID: 'y',
        data: this.periods.map(period => {
          const details = period.details || [];
          const found = details.find(d => d.destType === destType);
          return Number(found?.usage || 0);
        })
      };
    },
    getChfDataset(destType, color) {
      return {
        label: destType,
        backgroundColor: color,
        stack: 'chf',
        yAxisID: 'y',
        data: this.periods.map(period => {
          const details = period.details || [];
          const found = details.find(d => d.destType === destType);
          return Number(found?.sum || 0);
        })
      };
    },
    getChartOptions(type) {
      const scales = {
        x: {
          stacked: true,
          title: { display: true, text: this.mode === 'months' ? 'Months' : 'Days' }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          stacked: true,
          title: { display: true, text: type === 'SMS' ? 'SMS Sent' : 'CHF Total' }
        }
      };
      return {
        plugins: { title: { display: false } },
        responsive: true,
        aspectRatio: 3,
        maintainAspectRatio: true,
        scales
      };
    },
    getUsage(period, destType) {
      const details = period?.details || [];
      const found = details.find((detail) => detail.destType === destType);
      return Number(found?.usage || 0);
    },
    getTotalSum(period) {
      const details = period?.details || [];
      const total = details.reduce((sum, detail) => {
        return sum + Number(detail.sum || 0);
      }, 0);
      return Number(total.toFixed(2));
    }
  }
};
</script>

<style scoped>
.subtitle {
  margin-top: 0;
  color: #5a6673;
}

.label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #5a6673;
}

.filter-input {
  width: 100%;
  box-sizing: border-box;
}

.summary {
  display: flex;
  gap: 12px;
  margin: 16px 0;
}

.summary-card {
  min-width: 180px;
  border: 1px solid #d6dee8;
  background: #f7fafc;
  border-radius: 8px;
  padding: 12px 14px;
}

.summary-label {
  font-size: 13px;
  color: #536171;
}

.summary-value {
  font-size: 24px;
  font-weight: 700;
  color: #0c3b62;
}

.sms-statistic-chart {
  padding-top: 30px;
}

.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  text-align: left;
  padding: 10px;
  border-bottom: 1px solid #e8edf3;
}

th {
  background: #f1f5fa;
  color: #2b3745;
  font-weight: 600;
}
</style>
